import { useState, useEffect, useContext } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { AppContext } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Building2, Lock, MessageCircle, MapPin, Mail, FileText, Globe } from 'lucide-react';

export default function DatosCentro() {
  const { API_URL } = useContext(AppContext);
  const [loading, setLoading] = useState(true);
  const [companyData, setCompanyData] = useState(null);

  useEffect(() => {
    fetchCompanyData();
  }, []);

  const fetchCompanyData = async () => {
    try {
      const token = localStorage.getItem('konemed_token');
      const response = await axios.get(`${API_URL}/company/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompanyData(response.data);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleContactAdmin = () => {
    const message = `Solicito modificar los datos de mi centro:
- Código de Empresa: ${companyData?.codigo_empresa || 'N/A'}
- Razón Social: ${companyData?.razon_social || 'N/A'}

Por favor indique los cambios que necesita realizar.`;
    
    const whatsappUrl = `https://wa.me/573008799545?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  return (
    <DashboardLayout>
      <div data-testid="datos-centro-page">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-slate-900 mb-2" data-testid="page-title">
            Datos del Centro
          </h1>
          <p className="text-slate-600">Información que aparece en los consentimientos</p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-8">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-violet-100 rounded-lg flex items-center justify-center">
                      <Building2 className="text-violet-600" size={24} />
                    </div>
                    <h2 className="text-xl font-bold text-slate-900">Información de la Clínica</h2>
                  </div>
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-100 rounded-lg text-slate-500 text-sm">
                    <Lock size={14} />
                    Solo lectura
                  </div>
                </div>

                <div className="space-y-6">
                  {/* Código de Empresa */}
                  <div className="bg-violet-50 rounded-xl p-4 border border-violet-100">
                    <p className="text-sm text-violet-600 mb-1">Código de Empresa</p>
                    <p className="text-2xl font-mono font-bold text-violet-700" data-testid="display-codigo">
                      {companyData?.codigo_empresa || 'N/A'}
                    </p>
                  </div>

                  {/* Razón Social */}
                  <div>
                    <label className="block text-sm font-medium text-slate-500 mb-2">
                      Razón Social de la Empresa
                    </label>
                    <div className="w-full px-4 py-3 border border-slate-200 rounded-lg bg-slate-50 text-slate-900 font-medium" data-testid="display-razon-social">
                      {companyData?.razon_social || 'No configurado'}
                    </div>
                  </div>

                  {/* Nombre Fantasía */}
                  {companyData?.nombre_fantasia && (
                    <div>
                      <label className="block text-sm font-medium text-slate-500 mb-2">
                        Nombre de Fantasía
                      </label>
                      <div className="w-full px-4 py-3 border border-slate-200 rounded-lg bg-slate-50 text-slate-900 font-medium" data-testid="display-nombre-fantasia">
                        {companyData.nombre_fantasia}
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-6">
                    {/* Tipo de Documento */}
                    <div>
                      <label className="block text-sm font-medium text-slate-500 mb-2">
                        Tipo de Documento
                      </label>
                      <div className="w-full px-4 py-3 border border-slate-200 rounded-lg bg-slate-50 text-slate-900 font-medium flex items-center gap-2" data-testid="display-tipo-documento">
                        <FileText size={18} className="text-slate-400" />
                        {companyData?.tipo_documento || 'N/A'}
                      </div>
                    </div>

                    {/* Número de Documento */}
                    <div>
                      <label className="block text-sm font-medium text-slate-500 mb-2">
                        Número de Documento
                      </label>
                      <div className="w-full px-4 py-3 border border-slate-200 rounded-lg bg-slate-50 text-slate-900 font-mono font-medium" data-testid="display-numero-documento">
                        {companyData?.numero_documento || 'No configurado'}
                      </div>
                    </div>
                  </div>

                  {/* País */}
                  <div>
                    <label className="block text-sm font-medium text-slate-500 mb-2">
                      País
                    </label>
                    <div className="w-full px-4 py-3 border border-slate-200 rounded-lg bg-slate-50 text-slate-900 font-medium flex items-center gap-2" data-testid="display-pais">
                      <Globe size={18} className="text-slate-400" />
                      {companyData?.pais || 'No configurado'}
                    </div>
                  </div>

                  {/* Dirección */}
                  <div>
                    <label className="block text-sm font-medium text-slate-500 mb-2">
                      Dirección
                    </label>
                    <div className="w-full px-4 py-3 border border-slate-200 rounded-lg bg-slate-50 text-slate-900 font-medium flex items-center gap-2" data-testid="display-direccion">
                      <MapPin size={18} className="text-slate-400" />
                      {companyData?.direccion || 'No configurado'}
                    </div>
                  </div>

                  {/* Email de Respaldo */}
                  <div className="pt-6 border-t border-slate-200">
                    <h3 className="text-lg font-bold text-slate-900 mb-4">Respaldo de Consentimientos</h3>
                    <div>
                      <label className="block text-sm font-medium text-slate-500 mb-2">
                        Email de Respaldo
                      </label>
                      <div className="w-full px-4 py-3 border border-slate-200 rounded-lg bg-slate-50 text-slate-900 font-medium flex items-center gap-2" data-testid="display-email-respaldo">
                        <Mail size={18} className="text-slate-400" />
                        {companyData?.email_respaldo || 'No configurado'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-1 space-y-6">
              {/* Info Card */}
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Lock className="text-amber-600" size={20} />
                  <h3 className="text-lg font-bold text-amber-900">Datos Protegidos</h3>
                </div>
                <p className="text-sm text-amber-800 mb-4">
                  Los datos del centro solo pueden ser modificados por el administrador del sistema desde el Panel de Control.
                </p>
                <p className="text-sm text-amber-800">
                  Si necesita actualizar algún dato, contacte al administrador.
                </p>
              </div>

              {/* Contact Admin Button */}
              <div className="bg-white border border-slate-200 rounded-xl p-6">
                <h3 className="text-lg font-bold text-slate-900 mb-3">¿Necesita hacer cambios?</h3>
                <p className="text-sm text-slate-600 mb-4">
                  Contacte al administrador para solicitar la modificación de los datos de su centro.
                </p>
                <button
                  onClick={handleContactAdmin}
                  className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-lg transition-colors"
                  data-testid="btn-contact-admin"
                >
                  <MessageCircle size={20} />
                  Contactar Administrador
                </button>
              </div>

              {/* Info about data usage */}
              <div className="bg-violet-50 border border-violet-200 rounded-xl p-6">
                <h3 className="text-lg font-bold text-violet-900 mb-4">ℹ️ Uso de los Datos</h3>
                <div className="space-y-3 text-sm text-violet-800">
                  <p>
                    <strong>Razón Social:</strong> Aparece en el encabezado de los consentimientos.
                  </p>
                  <p>
                    <strong>Documento:</strong> Se incluye para identificación legal.
                  </p>
                  <p>
                    <strong>Dirección:</strong> Ubicación del centro en los documentos.
                  </p>
                  <p className="pt-3 border-t border-violet-300">
                    Estos datos aparecerán en todos los consentimientos generados por su centro.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
